import React from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from '../../i18n/translationKeys';
import { useLanguageStore } from '../../stores/languageStore';
import { describeSentRelance } from '../../utils/relance/describeSentRelance';
import { StatementSentNote } from './StatementSentNote';
import { InlineHistoryTimeline } from '../../components/History/InlineHistoryTimeline';
import type { HistoryEventItemDto } from '../../services/eventHistoryService';

interface CustomerFollowUpTimelineProps {
    customerId: string;
    // Reminders already sent: the exact number of RELANCE_COMPLETED events the box will list.
    relanceCount: number;
    statementSentOn?: string | null;
}

// Reminder flavour of the inline history box: shows the reminders already sent to a client
// (RELANCE_COMPLETED events), newest first, as "{date} · {Nᵉ} relance envoyée" + invoice/amount.
// The numéro de relance is derived from chronological position (oldest = 1re), so with a newest-first
// list the ordinal counts down from the top. Read statuses and inbound responses are not shown: that
// data is not captured yet.
export const CustomerFollowUpTimeline: React.FC<CustomerFollowUpTimelineProps> = ({ customerId, relanceCount, statementSentOn }) => {
    const { t } = useTranslation();
    const lang = useLanguageStore(s => s.lang);

    const renderEntry = (event: HistoryEventItemDto, index: number, events: HistoryEventItemDto[]): React.ReactNode => {
        if (event.type !== 'RELANCE_COMPLETED') return null;
        // events are newest-first; the oldest send is the 1re relance, so the chronological rank is
        // its distance from the end of the list.
        const { date, label, invoiceLine } = describeSentRelance(event, events.length - index, t, lang);
        return (
            <div data-testid={`follow-up-entry-${event.id}`} className="flex gap-m text-content-tertiary">
                <span aria-hidden="true" className="flex flex-col items-center shrink-0">
                    <span className="flex items-center h-lh">
                        <span className="size-1.25 rounded-full bg-primary-400" />
                    </span>
                    {/* Reaches through the list gap so the rule stops just short of the next bullet. */}
                    {index < events.length - 1 && <span className="w-px grow -mb-3.5 bg-primary-400" />}
                </span>
                <div className="min-w-0">
                    <p className="text-content-secondary">
                        {date} · {label}
                    </p>
                    <p>{invoiceLine}</p>
                </div>
            </div>
        );
    };

    return (
        <>
            <StatementSentNote sentOn={statementSentOn} className="mb-xs" />
            <InlineHistoryTimeline
                customerId={customerId}
                entryCount={relanceCount}
                eventType="RELANCE_COMPLETED"
                orderDirection="desc"
                testId={`follow-up-${customerId}`}
                labels={{
                    loading: t(TranslationKey.Home.FollowUp.Loading),
                    error: t(TranslationKey.Home.FollowUp.Error),
                }}
                renderEntry={renderEntry}
            />
        </>
    );
};
